import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Lock,
  Phone,
  Building,
  ArrowLeft,
  Briefcase,
  UserPlus,
  Eye,
  EyeOff,
  AlertCircle,
  Shield,
  Award,
  TrendingUp,
  Globe,
  Zap,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { userRegister } from "../../store/slices/authSlice";
import { useToast } from "../../components/common/ThemeToaster";

const UserRegister = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    success: toastSuccess,
    error: toastError,
    info: toastInfo,
  } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [apiErrors, setApiErrors] = useState({});

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: "",
    color: "",
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    company: "",
    position: "",
  });

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    confirmPassword: false,
    phone: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      validatePasswordStrength(value);
    }

    // Clear field error when user types
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
    if (apiErrors[name]) {
      setApiErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFocusedField(null);

    // Validate on blur
    const error = validateField(field, formData[field]);
    if (error) {
      setFieldErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleFocus = (field) => {
    setFocusedField(field);
  };

  const validatePasswordStrength = (password) => {
    const hasMinLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*]/.test(password);

    let score = 0;
    if (hasMinLength) score++;
    if (hasUpperCase) score++;
    if (hasLowerCase) score++;
    if (hasNumber) score++;
    if (hasSpecialChar) score++;

    let message = "";
    let color = "";
    if (score === 5) {
      message = "Strong";
      color = "text-green-600";
    } else if (score >= 3) {
      message = "Medium";
      color = "text-yellow-600";
    } else {
      message = "Weak";
      color = "text-red-600";
    }

    setPasswordStrength({
      score,
      message,
      color,
      hasMinLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar,
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Phone is optional
    // E.164 format: + followed by 10-15 digits
    const phoneRegex = /^\+\d{10,15}$/;
    return phoneRegex.test(phone);
  };

  // Single validation function for each field
  const validateField = (field, value) => {
    switch (field) {
      case "firstName":
        if (!value?.trim()) return "First name is required";
        return null;
      case "lastName":
        if (!value?.trim()) return "Last name is required";
        return null;
      case "email":
        if (!value) return "Valid email is required";
        if (!validateEmail(value)) return "Valid email is required";
        return null;
      case "password":
        if (!value) return "Password must be at least 8 characters";
        if (value.length < 8) return "Password must be at least 8 characters";
        return null;
      case "confirmPassword":
        if (!value) return "Please confirm your password";
        if (value !== formData.password) return "Passwords do not match";
        return null;
      case "phone":
        if (value && !validatePhone(value)) {
          return "Phone number must be in E.164 format (e.g., +1234567890)";
        }
        return null;
      default:
        return null;
    }
  };

  const getFieldError = (field) => {
    // Priority: API errors > local validation errors > touched validation
    if (apiErrors[field]) return apiErrors[field];
    if (fieldErrors[field]) return fieldErrors[field];
    if (!touched[field]) return null;
    return validateField(field, formData[field]);
  };

  const inputClasses = (field) => {
    const hasError = getFieldError(field);
    const isFocused = focusedField === field;

    return `w-full px-4 py-3.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border rounded-xl transition-all duration-200
      ${
        hasError
          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
          : isFocused
            ? "border-primary-500 ring-2 ring-primary-500/20"
            : "border-gray-200/50 dark:border-gray-700/50 focus:border-primary-500"
      } focus:outline-none focus:ring-2 transition-all text-gray-900 dark:text-white placeholder-gray-400`;
  };

  // Parse API errors and map to fields
  const parseApiErrors = (errors) => {
    const errorMap = {};
    if (Array.isArray(errors)) {
      errors.forEach((error) => {
        if (error.path && error.msg) {
          errorMap[error.path] = error.msg;
        }
      });
    }
    return errorMap;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      password: true,
      confirmPassword: true,
      phone: true,
    });

    // Validate all fields
    const newErrors = {};
    const fieldsToValidate = [
      "firstName",
      "lastName",
      "email",
      "password",
      "confirmPassword",
      "phone",
    ];

    fieldsToValidate.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setFieldErrors(newErrors);

    // Check if there are any validation errors
    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      toastError(firstError);
      return;
    }

    if (!agreedToTerms) {
      toastError("Please agree to the Terms of Service and Privacy Policy");
      return;
    }

    setIsLoading(true);
    setApiErrors({});

    try {
      await dispatch(
        userRegister({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          company: formData.company || undefined,
          position: formData.position || undefined,
        }),
      ).unwrap();

      toastSuccess("Registration successful! Redirecting to your profile...");
      toastInfo(`Welcome to TradeBridge, ${formData.firstName}!`);

      setTimeout(() => {
        navigate("/user/profile");
      }, 2000);
    } catch (err) {
      console.error("Registration error:", err);

      // Handle API validation errors
      if (err?.response?.data?.errors) {
        const apiErrorMap = parseApiErrors(err.response.data.errors);
        setApiErrors(apiErrorMap);

        // Show first API error as toast
        const firstApiError = Object.values(apiErrorMap)[0];
        if (firstApiError) {
          toastError(firstApiError);
        }
      } else {
        // Handle other errors (network, server, etc.)
        let errorMessage = "Registration failed. Please try again.";
        if (typeof err === "string") {
          errorMessage = err;
        } else if (err?.message) {
          errorMessage = err.message;
        } else if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        }

        toastError(errorMessage);

        // Handle specific error messages
        if (errorMessage.toLowerCase().includes("email already exists")) {
          setApiErrors((prev) => ({ ...prev, email: "Email already exists" }));
        } else if (
          errorMessage.toLowerCase().includes("phone number already registered")
        ) {
          setApiErrors((prev) => ({
            ...prev,
            phone: "Phone number already registered",
          }));
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { icon: TrendingUp, text: "Access to premium suppliers" },
    { icon: Globe, text: "Global trade network" },
    { icon: Shield, text: "Secure transactions" },
    { icon: Zap, text: "Fast response times" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl w-full relative z-10"
      >
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          onClick={() => navigate("/")}
          className="group flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary-600 transition-colors mb-6"
        >
          <div className="p-1.5 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 transition-colors">
            <ArrowLeft
              size={16}
              className="group-hover:text-primary-600 transition-colors"
            />
          </div>
          <span>Back to Home</span>
        </motion.button>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Side - Features */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-600 rounded-3xl p-8 h-full relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

              <div className="relative z-10">
                <div className="mb-8">
                  <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 mb-6">
                    <img
                      src="/Icon.png"
                      alt="TradeBridge Logo"
                      className="w-5 h-5 object-contain"
                    />
                    <span className="text-xs font-bold text-white uppercase tracking-wider">
                      TradeBridge
                    </span>
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-4">
                    Join the Global Trade Network
                  </h2>
                  <p className="text-white/90 leading-relaxed">
                    Create your account and start sourcing products from trusted
                    suppliers worldwide.
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="flex items-center gap-3 text-white/90"
                    >
                      <div className="p-1.5 bg-white/20 rounded-lg">
                        <feature.icon size={16} className="text-primary-600" />
                      </div>
                      <span className="text-sm">{feature.text}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="pt-6 border-t border-white/20">
                  <div className="flex items-center justify-center gap-2 text-white/80 text-xs">
                    <Award size={14} />
                    <span>Trusted by hundreds businesses worldwide</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Register Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden"
          >
            <div className="relative p-6 sm:p-8 bg-gradient-to-r from-primary-600/5 to-secondary-600/5 border-b border-gray-100 dark:border-gray-800">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-full mb-4">
                  <UserPlus size={14} className="text-primary-600" />
                  <span className="text-xs font-medium text-primary-600">
                    Create Account
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Get Started Today
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Join hundreds of businesses in the global trade network
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                      <User size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      onFocus={() => handleFocus("firstName")}
                      onBlur={() => handleBlur("firstName")}
                      className={`${inputClasses("firstName")} pl-10`}
                      placeholder="John"
                    />
                  </div>
                  {getFieldError("firstName") && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {getFieldError("firstName")}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                      <User size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      onFocus={() => handleFocus("lastName")}
                      onBlur={() => handleBlur("lastName")}
                      className={`${inputClasses("lastName")} pl-10`}
                      placeholder="Doe"
                    />
                  </div>
                  {getFieldError("lastName") && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {getFieldError("lastName")}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                      <Mail size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => handleFocus("email")}
                      onBlur={() => handleBlur("email")}
                      className={`${inputClasses("email")} pl-10`}
                      placeholder="john@company.com"
                    />
                  </div>
                  {getFieldError("email") && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {getFieldError("email")}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                      <Lock size={18} className="text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => handleFocus("password")}
                      onBlur={() => handleBlur("password")}
                      className={`${inputClasses("password")} pl-10 pr-10`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-10"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {getFieldError("password") && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {getFieldError("password")}
                    </p>
                  )}

                  {formData.password && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-3 space-y-2"
                    >
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                              i < passwordStrength.score
                                ? passwordStrength.score === 5
                                  ? "bg-green-500"
                                  : passwordStrength.score >= 3
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                                : "bg-gray-200 dark:bg-gray-700"
                            }`}
                          />
                        ))}
                      </div>
                      <p className={`text-xs ${passwordStrength.color}`}>
                        Password strength: {passwordStrength.message}
                      </p>
                    </motion.div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                      <Lock size={18} className="text-gray-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onFocus={() => handleFocus("confirmPassword")}
                      onBlur={() => handleBlur("confirmPassword")}
                      className={`${inputClasses("confirmPassword")} pl-10 pr-10`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-10"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {getFieldError("confirmPassword") && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {getFieldError("confirmPassword")}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                      <Phone size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => handleFocus("phone")}
                      onBlur={() => handleBlur("phone")}
                      className={`${inputClasses("phone")} pl-10`}
                      placeholder="+1234567890"
                    />
                  </div>
                  {getFieldError("phone") && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {getFieldError("phone")}
                    </p>
                  )}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company Name
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                      <Building size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 pl-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="Your Company Inc."
                    />
                  </div>
                </div>

                {/* Position */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Position
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                      <Briefcase size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 pl-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="Purchasing Manager"
                    />
                  </div>
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="mt-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    I agree to the{" "}
                    <Link
                      to="/terms"
                      className="text-primary-600 hover:underline"
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy"
                      className="text-primary-600 hover:underline"
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full mt-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg shadow-primary-500/20"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Creating account...</span>
                  </>
                ) : (
                  <>
                    <UserPlus size={20} />
                    <span>Create Account</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              {/* Footer */}
              <div className="mt-6 text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  Already have an account?{" "}
                  <Link
                    to="/user/login"
                    className="text-primary-600 font-medium hover:underline inline-flex items-center gap-1"
                  >
                    Sign in here
                    <ArrowRight size={14} />
                  </Link>
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default UserRegister;
